/**
 * 💳 HEARTBEATS.FR - Pricing Page
 * Page des tarifs et abonnements
 * ✅ Intégration Stripe Checkout
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Heart, Check, X, Sparkles, Crown, Zap, Star,
  ArrowRight, Shield, Gift, Infinity, MessageCircle,
  Eye, Volume2, Users, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HeartbeatsPricing = ({ onSelectPlan, onBack, currentTier = 'free', profileId }) => {
  const [billingCycle, setBillingCycle] = useState('monthly'); // monthly, yearly
  const [tiers, setTiers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Charger les tiers depuis l'API
  useEffect(() => {
    const fetchTiers = async () => {
      try {
        const res = await fetch(`${API_URL}/api/subscription/tiers`);
        if (res.ok) {
          const data = await res.json();
          setTiers(data.tiers);
        }
      } catch (err) {
        console.error('Erreur chargement tiers:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTiers();
  }, []);

  const plans = [
    {
      id: 'free',
      name: 'Gratuit',
      price: 0,
      yearlyPrice: 0,
      description: 'Pour découvrir Heartbeats',
      color: 'from-gray-500 to-gray-600',
      borderColor: 'border-gray-500/30',
      popular: false,
      features: [
        { text: '3 découvertes par jour', included: true },
        { text: '1 Super Like par jour', included: true },
        { text: 'Chat texte (10 msg/jour)', included: true },
        { text: 'Profil avec avatar', included: true },
        { text: 'Guides Heartbeats', included: true },
        { text: 'Découvertes illimitées', included: false },
        { text: 'Voir qui t\'a liké', included: false },
        { text: 'Salons vocaux', included: false },
        { text: 'Matching prioritaire', included: false },
        { text: 'Accusés de lecture', included: false },
      ]
    },
    {
      id: 'premium',
      name: 'Premium',
      price: 9.90,
      yearlyPrice: 99,
      description: 'L\'expérience complète',
      color: 'from-pink-500 to-purple-600',
      borderColor: 'border-pink-500/50',
      popular: true,
      badge: 'Le plus populaire',
      features: [
        { text: 'Découvertes illimitées', included: true, highlight: true },
        { text: '5 Super Likes par jour', included: true },
        { text: 'Chat texte illimité', included: true },
        { text: 'Profil avec avatar', included: true },
        { text: 'Guides Heartbeats', included: true },
        { text: 'Voir qui t\'a liké', included: true, highlight: true },
        { text: 'Salons vocaux', included: true, highlight: true },
        { text: 'Matching prioritaire', included: true },
        { text: 'Accusés de lecture', included: true },
        { text: 'Badge Premium', included: true },
      ]
    },
    {
      id: 'vip',
      name: 'VIP',
      price: 0,
      yearlyPrice: 0,
      description: 'Réservé aux partenaires',
      color: 'from-yellow-500 to-amber-600',
      borderColor: 'border-yellow-500/50',
      popular: false,
      badge: 'Exclusif',
      vipOnly: true,
      features: [
        { text: 'TOUT illimité', included: true, highlight: true },
        { text: 'Super Likes illimités', included: true },
        { text: 'Chat texte illimité', included: true },
        { text: 'Badge VIP exclusif', included: true, highlight: true },
        { text: 'Support prioritaire', included: true },
        { text: 'Accès anticipé nouveautés', included: true },
        { text: 'Voir qui t\'a liké', included: true },
        { text: 'Salons vocaux', included: true },
        { text: 'Matching prioritaire', included: true },
        { text: 'Accusés de lecture', included: true },
      ]
    }
  ];

  const handleSelectPlan = async (planId) => {
    if (planId === 'free' || planId === 'vip') {
      // Gratuit ou VIP - pas de paiement
      if (onSelectPlan) onSelectPlan(planId);
      return;
    }

    // Premium - créer une session Stripe Checkout
    if (!profileId) {
      toast.error('Tu dois être connecté pour t\'abonner');
      return;
    }

    setCheckoutLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/subscription/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          profile_id: profileId,
          tier: planId,
          billing_cycle: billingCycle,
          success_url: `${window.location.origin}/pricing?success=true`,
          cancel_url: `${window.location.origin}/pricing?canceled=true`
        })
      });

      const data = await response.json();

      if (response.ok && data.checkout_url) {
        // 🎉 Rediriger vers Stripe Checkout
        toast.success('Redirection vers le paiement...');
        window.location.href = data.checkout_url;
      } else {
        toast.error(data.detail || 'Erreur lors de la création du paiement');
      }
    } catch (err) {
      console.error('Erreur Stripe:', err);
      toast.error('Impossible de lancer le paiement. Réessaie plus tard.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  // Vérifier le statut du paiement au retour de Stripe
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      toast.success('🎉 Paiement réussi ! Bienvenue chez les Premium !');
      // Nettoyer l'URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (urlParams.get('canceled') === 'true') {
      toast.info('Paiement annulé. Tu peux réessayer quand tu veux.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  return (
    <div className="min-h-screen overflow-x-hidden overflow-y-auto bg-gradient-to-b from-[#0a0015] via-[#1a0033] to-[#0a0015] text-white" style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}>
      
      {/* Particules d'arrière-plan */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-pink-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      {/* Header */}
      <header className="relative z-10 container mx-auto px-4 py-6">
        <div className="flex items-center justify-between">
          <button 
            onClick={onBack}
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
          >
            <ArrowRight className="w-5 h-5 rotate-180" />
            Retour
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
              <Heart className="w-4 h-4 text-white" fill="white" />
            </div>
            <span className="text-lg font-bold">Heartbeats</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 container mx-auto px-4 py-12">
        
        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black mb-4">
            Choisis ton <span className="text-pink-400">aventure</span>
          </h1>
          <p className="text-white/60 max-w-xl mx-auto">
            Commence gratuitement et passe au niveau supérieur quand tu es prêt(e).
          </p>
        </motion.div>

        {/* Billing Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex justify-center mb-12"
        >
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-full p-1 flex">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                billingCycle === 'monthly' 
                  ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white' 
                  : 'text-white/60 hover:text-white'
              }`}
            >
              Mensuel
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                billingCycle === 'yearly' 
                  ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white' 
                  : 'text-white/60 hover:text-white'
              }`}
            >
              Annuel
              <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">-17%</span>
            </button>
          </div>
        </motion.div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans.map((plan, i) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + i * 0.1 }}
              className={`relative bg-white/5 backdrop-blur-sm border ${plan.borderColor} rounded-3xl p-6 ${
                plan.popular ? 'md:-mt-4 md:mb-4 md:scale-105' : ''
              }`}
            >
              {/* Badge */}
              {plan.badge && (
                <div className={`absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r ${plan.color} text-white text-xs font-bold px-4 py-1 rounded-full`}>
                  {plan.badge}
                </div>
              )}

              {/* Plan Header */}
              <div className="text-center mb-6 pt-2">
                <div className={`w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br ${plan.color} flex items-center justify-center mb-4`}>
                  {plan.id === 'free' && <Heart className="w-7 h-7 text-white" />}
                  {plan.id === 'premium' && <Zap className="w-7 h-7 text-white" />}
                  {plan.id === 'vip' && <Crown className="w-7 h-7 text-white" />}
                </div>
                <h3 className="text-2xl font-black">{plan.name}</h3>
                <p className="text-sm text-white/50">{plan.description}</p>
              </div>

              {/* Price */}
              <div className="text-center mb-6">
                {plan.vipOnly ? (
                  <div>
                    <div className="text-3xl font-black text-yellow-400">Exclusif</div>
                    <div className="text-sm text-white/50">Clients partenaires</div>
                  </div>
                ) : plan.price === 0 ? (
                  <div>
                    <div className="text-4xl font-black">Gratuit</div>
                    <div className="text-sm text-white/50">Pour toujours</div>
                  </div>
                ) : (
                  <div>
                    <div className="text-4xl font-black">
                      {billingCycle === 'yearly' 
                        ? `${plan.yearlyPrice}€` 
                        : `${plan.price}€`}
                    </div>
                    <div className="text-sm text-white/50">
                      {billingCycle === 'yearly' ? '/an' : '/mois'}
                    </div>
                    {billingCycle === 'yearly' && (
                      <div className="text-xs text-green-400 mt-1">
                        Soit {(plan.yearlyPrice / 12).toFixed(2)}€/mois
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, j) => (
                  <li key={j} className="flex items-start gap-3">
                    {feature.included ? (
                      <Check className={`w-5 h-5 flex-shrink-0 ${feature.highlight ? 'text-pink-400' : 'text-green-400'}`} />
                    ) : (
                      <X className="w-5 h-5 text-white/20 flex-shrink-0" />
                    )}
                    <span className={feature.included ? (feature.highlight ? 'text-white font-medium' : 'text-white/80') : 'text-white/30'}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              {!plan.vipOnly ? (
                <button
                  onClick={() => handleSelectPlan(plan.id)}
                  disabled={currentTier === plan.id || checkoutLoading}
                  className={`w-full py-3 rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${
                    plan.popular
                      ? 'bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white shadow-lg shadow-purple-500/25'
                      : currentTier === plan.id
                        ? 'bg-white/10 text-white/50 cursor-not-allowed'
                        : 'bg-white/10 hover:bg-white/20 text-white'
                  } ${checkoutLoading ? 'opacity-75 cursor-wait' : ''}`}
                >
                  {checkoutLoading && plan.id === 'premium' ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Chargement...
                    </>
                  ) : currentTier === plan.id ? (
                    'Plan actuel'
                  ) : plan.price === 0 ? (
                    'Commencer gratuitement'
                  ) : (
                    <>
                      <Zap className="w-5 h-5" />
                      Passer Premium
                    </>
                  )}
                </button>
              ) : (
                <div className="text-center text-sm text-white/50 py-3">
                  <Shield className="w-5 h-5 mx-auto mb-1 text-yellow-400" />
                  Réservé aux clients partenaires
                </div>
              )}
            </motion.div>
          ))}
        </div>

        {/* FAQ / Trust Badges */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-16 text-center"
        >
          <div className="flex flex-wrap justify-center gap-6 text-sm text-white/50">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-green-400" />
              Paiement sécurisé
            </div>
            <div className="flex items-center gap-2">
              <Gift className="w-4 h-4 text-pink-400" />
              Annulation à tout moment
            </div>
            <div className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-cyan-400" />
              Support 7j/7
            </div>
          </div>
        </motion.div>

        {/* Compare Features */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-24"
        >
          <h2 className="text-2xl font-black text-center mb-8">
            Comparaison détaillée
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full max-w-4xl mx-auto">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-4 px-4">Fonctionnalité</th>
                  <th className="text-center py-4 px-4">Gratuit</th>
                  <th className="text-center py-4 px-4 text-pink-400">Premium</th>
                  <th className="text-center py-4 px-4 text-yellow-400">VIP</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {[
                  { feature: 'Découvertes par jour', free: '3', premium: 'Illimité', vip: 'Illimité' },
                  { feature: 'Super Likes par jour', free: '1', premium: '5', vip: 'Illimité' },
                  { feature: 'Messages par jour', free: '10', premium: 'Illimité', vip: 'Illimité' },
                  { feature: 'Salons vocaux', free: '❌', premium: '✅', vip: '✅' },
                  { feature: 'Voir qui t\'a liké', free: '❌', premium: '✅', vip: '✅' },
                  { feature: 'Matching prioritaire', free: '❌', premium: '✅', vip: '✅' },
                  { feature: 'Accusés de lecture', free: '❌', premium: '✅', vip: '✅' },
                  { feature: 'Badge spécial', free: '❌', premium: '💜', vip: '👑' },
                  { feature: 'Support prioritaire', free: '❌', premium: '❌', vip: '✅' },
                ].map((row, i) => (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                    <td className="py-3 px-4 text-white/70">{row.feature}</td>
                    <td className="py-3 px-4 text-center">{row.free}</td>
                    <td className="py-3 px-4 text-center">{row.premium}</td>
                    <td className="py-3 px-4 text-center">{row.vip}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/10 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-white/30">
          © 2025 Heartbeats. Tous droits réservés.
        </div>
      </footer>
    </div>
  );
};

export default HeartbeatsPricing;
